import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./CounterpartiesDatabase.css";
import { UserContext } from "../../../contexts/CurrentUserContext";
import { getAllCounterparties } from "../../../api/api";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Компактный ряд страниц: 1 … 5 6 7 … 400 */
const getVisiblePages = (current, total, siblingCount = 1) => {
  if (total <= 0) return [];

  const maxVisible = siblingCount * 2 + 5;
  if (total <= maxVisible) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(current - siblingCount, 1);
  const rightSibling = Math.min(current + siblingCount, total);
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < total - 1;
  const pages = [];

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftCount = 3 + 2 * siblingCount;
    for (let i = 1; i <= leftCount; i += 1) pages.push(i);
    pages.push("ellipsis-right");
    pages.push(total);
  } else if (showLeftEllipsis && !showRightEllipsis) {
    pages.push(1);
    pages.push("ellipsis-left");
    const rightCount = 3 + 2 * siblingCount;
    for (let i = total - rightCount + 1; i <= total; i += 1) pages.push(i);
  } else {
    pages.push(1);
    pages.push("ellipsis-left");
    for (let i = leftSibling; i <= rightSibling; i += 1) pages.push(i);
    pages.push("ellipsis-right");
    pages.push(total);
  }

  return pages;
};

const CounterpartiesDatabase = () => {
  const { userData } = useContext(UserContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("counterparties");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Доступ: АДМС, АДМП, ФИН
  const hasAccess = userData?.access_levels?.some((level) =>
    ["АДМС", "АДМП", "ФИН"].includes(
      typeof level === "string" ? level : level?.name
    )
  );

  const [counterparties, setCounterparties] = useState([]);
  const [ourOrganizations, setOurOrganizations] = useState([]);
  const [loadingCounterparties, setLoadingCounterparties] = useState(false);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [errorCounterparties, setErrorCounterparties] = useState(null);
  const [errorOrganizations, setErrorOrganizations] = useState(null);

  // Загрузка данных контрагентов при переключении на вкладку "Контрагенты"
  useEffect(() => {
    const loadCounterparties = async () => {
      if (activeTab === "counterparties") {
        setLoadingCounterparties(true);
        setErrorCounterparties(null);
        try {
          const counterpartiesData = await getAllCounterparties({
            counterparty_side: "counterparty",
          });
          // Преобразуем данные контрагентов в формат для отображения (поля API: full_company_name, short_company_name, full_name, bin_or_inn, full_name_first_supervisor)
          const formattedCounterparties = Array.isArray(counterpartiesData)
            ? counterpartiesData.map((counterparty) => {
                let responsible = "—";
                if (counterparty.contact_person) {
                  responsible = counterparty.contact_person;
                } else if (counterparty.full_name_first_supervisor) {
                  responsible = counterparty.job_title
                    ? `${counterparty.full_name_first_supervisor} (${counterparty.job_title})`
                    : `${counterparty.full_name_first_supervisor} (директор)`;
                } else if (counterparty.director_full_name) {
                  responsible = `${counterparty.director_full_name} (директор)`;
                } else if (
                  counterparty.type === "physical_entity" &&
                  counterparty.full_name
                ) {
                  // ИП и физлицо на ГПХ: в «Ответственный» подставляем ФИО
                  responsible = counterparty.full_name;
                }
                const name =
                  counterparty.full_name ||
                  counterparty.full_company_name ||
                  counterparty.short_company_name ||
                  counterparty.short_name ||
                  "—";
                return {
                  id: counterparty.id,
                  entityType: counterparty.type || "legal_entity",
                  name,
                  type: counterparty.legal_form || "—",
                  bin: counterparty.bin_or_inn || counterparty.bin_iin || "—",
                  vat: counterparty.is_nds_payer || false,
                  taxRegime: counterparty.tax_regime || "—",
                  responsible,
                };
              })
            : [];
          setCounterparties(formattedCounterparties);
        } catch (error) {
          console.error("Ошибка при загрузке контрагентов:", error);
          setErrorCounterparties("Не удалось загрузить данные контрагентов");
          setCounterparties([]);
        } finally {
          setLoadingCounterparties(false);
        }
      }
    };

    loadCounterparties();
  }, [activeTab]);

  // Загрузка наших компаний при переключении на вкладку "Наши организации" (counterparty_side: our_company)
  useEffect(() => {
    const loadOrganizations = async () => {
      if (activeTab === "organizations") {
        setLoadingOrganizations(true);
        setErrorOrganizations(null);
        try {
          const companiesData = await getAllCounterparties({
            counterparty_side: "our_company",
          });
          // Тот же формат, что и у контрагентов (API контрагентов)
          const formattedOrganizations = Array.isArray(companiesData)
            ? companiesData.map((counterparty) => {
                let responsible = "—";
                if (counterparty.contact_person) {
                  responsible = counterparty.contact_person;
                } else if (counterparty.full_name_first_supervisor) {
                  responsible = counterparty.job_title
                    ? `${counterparty.full_name_first_supervisor} (${counterparty.job_title})`
                    : `${counterparty.full_name_first_supervisor} (директор)`;
                } else if (counterparty.director_full_name) {
                  responsible = `${counterparty.director_full_name} (директор)`;
                } else if (
                  counterparty.type === "physical_entity" &&
                  counterparty.full_name
                ) {
                  responsible = counterparty.full_name;
                }
                const name =
                  counterparty.full_name ||
                  counterparty.full_company_name ||
                  counterparty.short_company_name ||
                  counterparty.short_name ||
                  "—";
                return {
                  id: counterparty.id,
                  entityType: counterparty.type || "legal_entity",
                  name,
                  type: counterparty.legal_form || "—",
                  bin: counterparty.bin_or_inn || counterparty.bin_iin || "—",
                  vat: counterparty.is_nds_payer || false,
                  taxRegime: counterparty.tax_regime || "—",
                  responsible,
                };
              })
            : [];
          setOurOrganizations(formattedOrganizations);
        } catch (error) {
          console.error("Ошибка при загрузке наших компаний:", error);
          setErrorOrganizations("Не удалось загрузить данные наших компаний");
          setOurOrganizations([]);
        } finally {
          setLoadingOrganizations(false);
        }
      }
    };

    loadOrganizations();
  }, [activeTab]);

  // Фильтрация данных по поисковому запросу
  const filteredData =
    activeTab === "counterparties"
      ? counterparties.filter((item) => {
          const query = searchQuery.toLowerCase();
          return (
            item.name.toLowerCase().includes(query) || item.bin.includes(query)
          );
        })
      : ourOrganizations.filter((item) => {
          const query = searchQuery.toLowerCase();
          return (
            item.name.toLowerCase().includes(query) || item.bin.includes(query)
          );
        });

  // Пагинация
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);
  const visiblePages = useMemo(
    () => getVisiblePages(currentPage, totalPages),
    [currentPage, totalPages]
  );

  // Сброс страницы при изменении поиска, вкладки или размера страницы
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeTab, itemsPerPage]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  if (!hasAccess) {
    return (
      <div className="counterparties-database-container">
        <div className="counterparties-access-denied">
          У вас нет доступа к этой странице
        </div>
      </div>
    );
  }

  return (
    <div className="counterparties-database-container">
      <div className="counterparties-header">
        <h1 className="counterparties-title">База данных контрагентов</h1>
        {activeTab === "counterparties" && (
          <button
            className="counterparties-add-btn"
            onClick={() =>
              navigate("/projects/counterparties/add?type=counterparty")
            }
          >
            + Добавить контрагента
          </button>
        )}
        {activeTab === "organizations" && (
          <button
            className="counterparties-add-btn"
            onClick={() =>
              navigate("/projects/counterparties/add?type=organization")
            }
          >
            + Добавить организацию
          </button>
        )}
      </div>

      <div className="counterparties-tabs">
        <button
          className={`counterparties-tab-btn ${
            activeTab === "counterparties" ? "active" : ""
          }`}
          onClick={() => setActiveTab("counterparties")}
        >
          Контрагенты
        </button>
        <button
          className={`counterparties-tab-btn ${
            activeTab === "organizations" ? "active" : ""
          }`}
          onClick={() => setActiveTab("organizations")}
        >
          Наши организации
        </button>
      </div>

      <div className="counterparties-search-container">
        <div className="counterparties-search-wrapper">
          <svg
            className="counterparties-search-icon"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
              stroke="#6C757D"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M17.5 17.5L13.875 13.875"
              stroke="#6C757D"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <input
            type="text"
            className="counterparties-search-input"
            placeholder="Поиск по названию или БИН/ИИН..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="counterparties-table-container">
        {(loadingCounterparties && activeTab === "counterparties") ||
        (loadingOrganizations && activeTab === "organizations") ? (
          <div className="counterparties-loading">Загрузка данных...</div>
        ) : (errorCounterparties && activeTab === "counterparties") ||
          (errorOrganizations && activeTab === "organizations") ? (
          <div className="counterparties-error">
            {activeTab === "counterparties"
              ? errorCounterparties
              : errorOrganizations}
          </div>
        ) : (
          <table className="counterparties-table">
            <thead>
              <tr>
                <th>№</th>
                <th>Наименование</th>
                <th>Тип</th>
                <th>БИН/ИИН</th>
                <th>НДС</th>
                <th>Режим налогооблажения</th>
                <th>Ответственный</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData.map((item, index) => (
                  <tr
                    key={item.id}
                    className="counterparties-table-row-clickable"
                    onClick={() => {
                      const entity = item.entityType || "legal_entity";
                      if (activeTab === "counterparties") {
                        navigate(
                          `/projects/counterparties/${item.id}?type=counterparty&entity=${entity}`
                        );
                      } else if (activeTab === "organizations") {
                        navigate(
                          `/projects/counterparties/${item.id}?type=organization&entity=${entity}`
                        );
                      }
                    }}
                  >
                    <td>{startIndex + index + 1}</td>
                    <td>{item.name}</td>
                    <td>{item.type}</td>
                    <td>{item.bin}</td>
                    <td>{item.vat ? "Да" : "Нет"}</td>
                    <td>{item.taxRegime}</td>
                    <td>{item.responsible}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="counterparties-empty">
                    Данные не найдены
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {filteredData.length > 0 && (
        <div className="counterparties-footer">
          {totalPages > 1 && (
            <div className="counterparties-pagination">
              <button
                type="button"
                className="counterparties-pagination-btn"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                aria-label="Предыдущая страница"
              >
                &lt;
              </button>
              {visiblePages.map((page) =>
                typeof page === "string" ? (
                  <span
                    key={page}
                    className="counterparties-pagination-ellipsis"
                    aria-hidden="true"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={page}
                    type="button"
                    className={`counterparties-pagination-btn ${
                      currentPage === page ? "active" : ""
                    }`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                )
              )}
              <button
                type="button"
                className="counterparties-pagination-btn"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
                aria-label="Следующая страница"
              >
                &gt;
              </button>
            </div>
          )}
          <div className="counterparties-footer-right">
            <label className="counterparties-page-size">
              На странице
              <select
                className="counterparties-page-size-select"
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
            <div className="counterparties-info">
              {`Показано: ${startIndex + 1}–${Math.min(
                endIndex,
                filteredData.length
              )} из ${filteredData.length}`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CounterpartiesDatabase;
